import { motion } from 'framer-motion';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';

import { Breadcrumb } from './breadcrumb';
import { IProduct } from '../types/products';
import { React360Viewer } from 'react-360-product-viewer';

interface IProductCard {
  product: IProduct;
  producer?: string;
}

export const PvcWindowContent = ({ product, producer }: IProductCard) => {
  const { t } = useTranslation('common');
  return (
    <div>
      <Breadcrumb product={product?.title} />
      <h1 className="text-3xl mb-2 p-1 bg-gradient-to-r from-red to-orange text-white font-bold">
        {producer ? <span className="uppercase">{producer}</span> : null}{' '}
        {product?.title ? product?.title : product?.en?.title}
      </h1>
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="order-last md:order-first">
          <img
            src={
              product?.PVCDetails?.photo?.sourceUrl
                ? product?.PVCDetails?.photo?.sourceUrl
                : product?.en?.PVCDetails?.photo?.sourceUrl
            }
          />
        </div>
        <div className="flex justify-around flex-col gap-6 md:gap-0 py-6">
          <div>
            {producer && (
              <Image
                src={`/img/partners/${producer}.png`}
                alt={producer}
                width={151}
                height={181}
              />
            )}
            <h4 className="text-5xl md:text-7xl font-bold">
              {product?.PVCDetails?.h1
                ? product?.PVCDetails?.h1
                : product?.en?.PVCDetails?.h1}
            </h4>
            <h5 className="text-7xl font-normal">
              {product?.PVCDetails?.h2
                ? product?.PVCDetails?.h2
                : product?.en?.PVCDetails?.h2}
            </h5>
            <h6 className="text-2xl font-thin">
              {product?.PVCDetails?.h3
                ? product?.PVCDetails?.h3
                : product?.en?.PVCDetails?.h3}
            </h6>
          </div>
          <div className="flex justify-center">
            {product?.en
              ? product?.en?.PVCDetails?.options?.map((option: any) => (
                  <motion.div
                    key={option?.option}
                    className="p-2"
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    <Image
                      alt={option}
                      src={`/img/pvc/${option}.png`}
                      width={95}
                      height={95}
                    />
                  </motion.div>
                ))
              : product?.PVCDetails?.options?.map((option: any) => (
                  <motion.div
                    key={option?.option}
                    className="p-2"
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    <Image
                      alt={option}
                      src={`/img/pvc/${option}.png`}
                      width={95}
                      height={95}
                    />
                  </motion.div>
                ))}
          </div>
          {product?.PVCDetails?.pdf?.mediaItemUrl ||
          product?.en?.PVCDetails?.pdf?.mediaItemUrl ? (
            <div className="mx-auto">
              <a
                href={
                  product?.PVCDetails?.pdf?.mediaItemUrl
                    ? product?.PVCDetails?.pdf?.mediaItemUrl
                    : product?.en?.PVCDetails?.pdf?.mediaItemUrl
                }
                target="_blank"
                rel="noreferrer noopener"
                className="bg-gradient-to-r hover:bg-gradient-to-t from-red to-orange text-white font-bold px-5 py-2 rounded-md text-xl"
              >
                POBIERZ ULOTKĘ
              </a>
            </div>
          ) : null}
        </div>
      </div>
      {product?.title === 'Elegant Abstract 76' && (
        <div style={{ textAlign: 'center' }}>
          <React360Viewer
            width={640}
            imagesBaseUrl="/img/360/"
            imagesCount={37}
            imagesFiletype="png"
            mouseDragSpeed={20}
            imageFilenamePrefix={''}
          />
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 py-6">
        {product?.en
          ? product?.en?.PVCDetails?.functions?.map((option: any) => (
              <div className="text-orange text-center mx-auto my-1">
                <motion.div
                  key={option?.option}
                  className="p-2"
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                >
                  <Image
                    src={`/img/functions/${option}.png`}
                    width={128}
                    height={128}
                    alt={option}
                    className="mx-auto"
                  />
                </motion.div>
                <strong className="block text-sm my-4">
                  {t(`functions.${option}`)}
                </strong>
              </div>
            ))
          : product?.PVCDetails?.functions?.map((option: any) => (
              <div className="text-orange text-center mx-auto my-1">
                <motion.div
                  key={option?.option}
                  className="p-2"
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                >
                  <Image
                    src={`/img/functions/${option}.png`}
                    width={128}
                    height={128}
                    alt={option}
                    className="mx-auto"
                  />
                </motion.div>
                <strong className="block text-sm my-4">
                  {t(`functions.${option}`)}
                </strong>
              </div>
            ))}
      </div>
      <div
        className="my-8"
        dangerouslySetInnerHTML={{
          __html: product?.content ? product?.content : product?.en?.content,
        }}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 whitespace-pre">
        <div>
          {product?.PVCDetails?.technicalDetails?.thermalIsolation ||
          product?.en?.PVCDetails?.technicalDetails?.thermalIsolation ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">
                {t('params.themralIsolation')}
              </strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.thermalIsolation
                  ? product?.PVCDetails?.technicalDetails?.thermalIsolation
                  : product?.en?.PVCDetails?.technicalDetails?.thermalIsolation}
              </div>
              <br />
            </>
          ) : null}
          {product?.PVCDetails?.technicalDetails?.tightness ||
          product?.en?.PVCDetails?.technicalDetails?.tightness ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">{t('params.tightness')}</strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.tightness
                  ? product?.PVCDetails?.technicalDetails?.tightness
                  : product?.en?.PVCDetails?.technicalDetails?.tightness}
              </div>
              <br />
            </>
          ) : null}
          {product?.PVCDetails?.technicalDetails?.acoustics ||
          product?.en?.PVCDetails?.technicalDetails?.acoustics ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">{t('params.acoustics')}</strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.acoustics
                  ? product?.PVCDetails?.technicalDetails?.acoustics
                  : product?.en?.PVCDetails?.technicalDetails?.acoustics}
              </div>
              <br />
            </>
          ) : null}
          {product?.PVCDetails?.technicalDetails?.security ||
          product?.en?.PVCDetails?.technicalDetails?.security ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">{t('functions.security')}</strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.security
                  ? product?.PVCDetails?.technicalDetails?.security
                  : product?.en?.PVCDetails?.technicalDetails?.security}
              </div>
              <br />
            </>
          ) : null}
          {product?.PVCDetails?.technicalDetails?.glazing ||
          product?.en?.PVCDetails?.technicalDetails?.glazing ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">{t('params.glazing')}</strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.glazing
                  ? product?.PVCDetails?.technicalDetails?.glazing
                  : product?.en?.PVCDetails?.technicalDetails?.glazing}
              </div>
              <br />
            </>
          ) : null}
          {product?.PVCDetails?.technicalDetails?.resistance ||
          product?.en?.PVCDetails?.technicalDetails?.resistance ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">{t('functions.resistance')}</strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.resistance
                  ? product?.PVCDetails?.technicalDetails?.resistance
                  : product?.en?.PVCDetails?.technicalDetails?.resistance}
              </div>
              <br />
            </>
          ) : null}
          {product?.PVCDetails?.technicalDetails?.waterprof ||
          product?.en?.PVCDetails?.technicalDetails?.waterprof ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">{t('params.waterproof')}</strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.waterprof
                  ? product?.PVCDetails?.technicalDetails?.waterprof
                  : product?.en?.PVCDetails?.technicalDetails?.waterprof}
              </div>
              <br />
            </>
          ) : null}
          {product?.PVCDetails?.technicalDetails?.grip ||
          product?.en?.PVCDetails?.technicalDetails?.grip ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">Pochwyt</strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.grip
                  ? product?.PVCDetails?.technicalDetails?.grip
                  : product?.en?.PVCDetails?.technicalDetails?.grip}
              </div>
              <br />
            </>
          ) : null}
          {product?.PVCDetails?.technicalDetails?.decoration ||
          product?.en?.PVCDetails?.technicalDetails?.decoration ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">Zdobienie</strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.decoration
                  ? product?.PVCDetails?.technicalDetails?.decoration
                  : product?.en?.PVCDetails?.technicalDetails?.decoration}
              </div>
              <br />
            </>
          ) : null}
          {product?.PVCDetails?.technicalDetails?.surface ||
          product?.en?.PVCDetails?.technicalDetails?.surface ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">Powierzchnia</strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.surface
                  ? product?.PVCDetails?.technicalDetails?.surface
                  : product?.en?.PVCDetails?.technicalDetails?.surface}
              </div>
              <br />
            </>
          ) : null}
          {product?.PVCDetails?.technicalDetails?.others ||
          product?.en?.PVCDetails?.technicalDetails?.others ? (
            <>
              <span className="text-orange mr-1">■</span>
              <strong className="uppercase">{t('params.other')}</strong>
              <br />
              <div className="ml-3">
                {product?.PVCDetails?.technicalDetails?.others
                  ? product?.PVCDetails?.technicalDetails?.others
                  : product?.en?.PVCDetails?.technicalDetails?.others}
              </div>
            </>
          ) : null}
          {/* <button className="bg-gradient-to-r from-red to-orange text-white font-bold px-4 py-2">
              PEŁNA SPECYFIKACJA
            </button> */}
          <br />
          <br />
          <strong className="uppercase">{t('optionalEqipment')}</strong>
          <br />
          <br />
          <div className="flex flex-row whitespace-normal">
            {product?.en
              ? product?.en?.PVCDetails?.additionalOptions?.map(
                  (option: any) => (
                    <div className="w-36">
                      <motion.div
                        key={option?.option}
                        className="p-2"
                        whileHover={{ scale: 1.05 }}
                        transition={{
                          type: 'spring',
                          stiffness: 400,
                          damping: 10,
                        }}
                      >
                        <Image
                          alt={option}
                          src={`/img/pvc/options/${option}.jpg`}
                          width={150}
                          height={150}
                        />
                      </motion.div>
                      <div className=" text-xs text-center break-normal">
                        {t(`additionalOptions.${option}`)}
                      </div>
                    </div>
                  )
                )
              : product?.PVCDetails?.additionalOptions?.map((option: any) => (
                  <div className="w-36">
                    <motion.div
                      key={option?.option}
                      className="p-2"
                      whileHover={{ scale: 1.05 }}
                      transition={{
                        type: 'spring',
                        stiffness: 400,
                        damping: 10,
                      }}
                    >
                      <Image
                        alt={option}
                        src={`/img/pvc/options/${option}.jpg`}
                        width={150}
                        height={150}
                      />
                    </motion.div>
                    <div className=" text-xs text-center break-normal">
                      {t(`additionalOptions.${option}`)}
                    </div>
                  </div>
                ))}
          </div>
        </div>
        <div>
          {product?.PVCDetails?.image3d?.sourceUrl && (
            <img
              src={
                product?.PVCDetails?.image3d?.sourceUrl
                  ? product?.PVCDetails?.image3d?.sourceUrl
                  : product?.en?.PVCDetails?.image3d?.sourceUrl
              }
            />
          )}
        </div>
      </div>
      <div className="my-10 product-features">
        <strong className="uppercase">{t('productFeatures')}</strong>
        <div
          dangerouslySetInnerHTML={{
            __html: product?.PVCDetails?.productFeatures
              ? product?.PVCDetails?.productFeatures
              : product?.en?.PVCDetails?.productFeatures,
          }}
        />
      </div>
    </div>
  );
};
